/* ==========================================================================
   Maštarije Nova — mst-search.js
   Header search modal + predictive search (progressive enhancement).

   <mst-search-modal>      — wraps the <details> search panel: focus trap,
                             Esc / overlay / outside-click close, scroll lock,
                             aria-expanded sync on the summary toggle.
                             Dialog semantics (role="dialog" + aria-modal) are
                             added on open and removed on close — never static
                             in the markup, because the no-JS fallback renders
                             the panel as a plain inline disclosure with no
                             focus trap (same pattern as MstFacets.openDrawer/
                             closeDrawer in snippets/facets.liquid).
   <mst-predictive-search> — debounced suggestions from /search/suggest.json
                             (matching collections as a small "Kolekcije"
                             group, then products: thumb + title + price),
                             keyboard navigable (combobox + listbox pattern;
                             listbox > group > option when grouped), results
                             announced via aria-live. The underlying form
                             still submits natively (GET /search) without JS.

   All strings arrive translated via data attributes (locale keys rendered
   in snippets/header-search.liquid). window.MST (mst-core.js) is used for
   trapFocus/releaseFocus/formatMoney when available.
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------------------
     <mst-search-modal>
     ------------------------------------------------------------------------ */
  if (!customElements.get('mst-search-modal')) {
    customElements.define(
      'mst-search-modal',
      class MstSearchModal extends HTMLElement {
        connectedCallback() {
          this.details = this.querySelector('details');
          if (!this.details) return;
          this.summary = this.details.querySelector('summary');
          this.panel = this.querySelector('[data-search-panel]');
          this.input = this.querySelector('[data-search-input]');

          /* The summary ships without aria-expanded (native <details>
             semantics cover the no-JS path); once JS runs, manage it. */
          if (this.summary) {
            this.summary.setAttribute('aria-expanded', this.details.open ? 'true' : 'false');
          }

          this.onToggleBound = this.onToggle.bind(this);
          this.onDocPointerBound = this.onDocPointer.bind(this);

          this.details.addEventListener('toggle', this.onToggleBound);

          this.addEventListener('keyup', (event) => {
            if (event.key === 'Escape') this.close();
          });

          this.querySelectorAll('[data-search-close]').forEach((btn) => {
            btn.addEventListener('click', this.close.bind(this));
          });

          var overlay = this.querySelector('[data-search-overlay]');
          if (overlay) overlay.addEventListener('click', this.close.bind(this));
        }

        disconnectedCallback() {
          document.removeEventListener('pointerdown', this.onDocPointerBound);
          document.documentElement.classList.remove('mst-scroll-lock');
        }

        onToggle() {
          if (this.details.open) {
            document.documentElement.classList.add('mst-scroll-lock');
            if (this.summary) this.summary.setAttribute('aria-expanded', 'true');
            /* Modal only while JS drives it: dialog semantics on open,
               removed again on close (mirrors facets.liquid). */
            if (this.panel) {
              this.panel.setAttribute('role', 'dialog');
              this.panel.setAttribute('aria-modal', 'true');
            }
            document.addEventListener('pointerdown', this.onDocPointerBound);
            if (window.MST && window.MST.trapFocus && this.panel) {
              window.MST.trapFocus(this.panel, this.input);
            } else if (this.input) {
              this.input.focus();
            }
          } else {
            document.documentElement.classList.remove('mst-scroll-lock');
            if (this.summary) this.summary.setAttribute('aria-expanded', 'false');
            if (this.panel) {
              this.panel.removeAttribute('role');
              this.panel.removeAttribute('aria-modal');
            }
            document.removeEventListener('pointerdown', this.onDocPointerBound);
            if (window.MST && window.MST.releaseFocus) {
              window.MST.releaseFocus(this.summary);
            } else if (this.summary) {
              this.summary.focus();
            }
          }
        }

        onDocPointer(event) {
          if (this.details.open && !this.contains(event.target)) this.close();
        }

        close() {
          if (this.details && this.details.open) this.details.open = false;
        }
      }
    );
  }

  /* ------------------------------------------------------------------------
     <mst-predictive-search>
     ------------------------------------------------------------------------ */
  if (!customElements.get('mst-predictive-search')) {
    customElements.define(
      'mst-predictive-search',
      class MstPredictiveSearch extends HTMLElement {
        connectedCallback() {
          this.input = this.querySelector('[data-search-input]');
          this.resultsEl = this.querySelector('[data-predictive-results]');
          this.statusEl = this.querySelector('[data-predictive-status]');
          if (!this.input || !this.resultsEl) return;

          this.url = this.dataset.url || '/search/suggest.json';
          this.searchUrl = this.dataset.searchUrl || '/search';
          this.limit = parseInt(this.dataset.limit, 10) || 8;

          this.debounceTimer = null;
          this.abortController = null;
          this.options = [];
          this.activeIndex = -1;
          this.lastQuery = '';

          this.input.addEventListener('input', () => {
            window.clearTimeout(this.debounceTimer);
            this.debounceTimer = window.setTimeout(this.search.bind(this), 300);
          });

          this.input.addEventListener('keydown', this.onKeydown.bind(this));
        }

        disconnectedCallback() {
          window.clearTimeout(this.debounceTimer);
          if (this.abortController) this.abortController.abort();
        }

        getQuery() {
          return this.input.value.trim();
        }

        setStatus(text) {
          if (this.statusEl) this.statusEl.textContent = text || '';
        }

        clearResults() {
          this.resultsEl.innerHTML = '';
          this.options = [];
          this.activeIndex = -1;
          this.input.setAttribute('aria-expanded', 'false');
          this.input.removeAttribute('aria-activedescendant');
          this.setStatus('');
        }

        search() {
          var query = this.getQuery();
          if (this.abortController) this.abortController.abort();

          if (query.length === 0) {
            this.clearResults();
            this.lastQuery = '';
            return;
          }
          if (query === this.lastQuery) return;
          this.lastQuery = query;

          this.abortController = 'AbortController' in window ? new AbortController() : null;
          this.setStatus(this.dataset.loading || '');

          // ux-cro-structure-11: collections and query suggestions as well.
          // limit_scope=each keeps this.limit per type; with the default
          // ("all") the limit is shared, so each matching collection used to
          // cost one product.
          var params =
            '?q=' +
            encodeURIComponent(query) +
            '&resources%5Btype%5D=product,collection,query' +
            '&resources%5Blimit%5D=' +
            this.limit +
            '&resources%5Blimit_scope%5D=each' +
            '&resources%5Boptions%5D%5Bunavailable_products%5D=last';

          var options = { headers: { Accept: 'application/json' } };
          if (this.abortController) options.signal = this.abortController.signal;

          var self = this;
          fetch(this.url + params, options)
            .then(function (response) {
              if (!response.ok) throw new Error(String(response.status));
              return response.json();
            })
            .then(function (data) {
              var results = (data && data.resources && data.resources.results) || {};
              var products = results.products || [];
              // ux-cro-structure-11: matching collections ("božić" → Božićni
              // ukrasi) lead as a small group, three at most so the products
              // stay in view. Query suggestions are not shown.
              var collections = (results.collections || []).slice(0, 3);
              // ux-visual-19: the request is capped at this.limit, so a full
              // page means "more on /search", not the real total.
              var capped = products.length >= self.limit;
              // Catalogue-hidden add-ons (paint/brush sets sold only from the
              // product page that offers them) must not surface here either.
              // The suggest API has no server-side exclusion, so filter on
              // the handle list the section renders into data-hidden-handles.
              var hidden = self.dataset.hiddenHandles || '';
              if (hidden.length > 2) {
                products = products.filter(function (product) {
                  return hidden.indexOf(',' + product.handle + ',') === -1;
                });
              }
              self.render(products, collections, query, capped);
            })
            .catch(function (error) {
              if (error && error.name === 'AbortError') return;
              self.resultsEl.innerHTML = '';
              self.options = [];
              self.activeIndex = -1;
              self.input.setAttribute('aria-expanded', 'false');
              // The visible status line shows (and announces) the message once.
              self.setStatus(self.dataset.error || '');
            });
        }

        formatPrice(raw) {
          if (raw === null || raw === undefined || raw === '') return '';
          var cents = Math.round(parseFloat(String(raw).replace(',', '.')) * 100);
          if (isNaN(cents)) return '';
          if (window.MST && window.MST.formatMoney) return window.MST.formatMoney(cents);
          return (cents / 100).toFixed(2);
        }

        thumbUrl(image) {
          if (!image || !image.url) return '';
          return image.url + (image.url.indexOf('?') === -1 ? '?' : '&') + 'width=120';
        }

        /* One listbox option around a link. Ids follow DOM order across the
           groups, so aria-activedescendant and the arrow keys walk every
           result from top to bottom. */
        addOption(parent, link, modifier) {
          var li = document.createElement('li');
          li.className = 'mst-search__item' + (modifier ? ' ' + modifier : '');
          li.id = 'MstPredictiveOption-' + this.options.length;
          li.setAttribute('role', 'option');
          li.setAttribute('aria-selected', 'false');
          li.appendChild(link);
          parent.appendChild(li);
          this.options.push(li);
        }

        /* ux-cro-structure-11: listbox > group > option. The visible label is
           aria-hidden and names the group through aria-labelledby, so screen
           readers hear it once, as the group name, never as an option. The
           inner <ul> is presentational; only the role="option" items count. */
        addGroup(list, key, label) {
          var group = document.createElement('li');
          group.className = 'mst-search__group';
          group.setAttribute('role', 'group');
          group.setAttribute('aria-labelledby', 'MstPredictiveGroup-' + key);

          var heading = document.createElement('span');
          heading.className = 'mst-search__group-label';
          heading.id = 'MstPredictiveGroup-' + key;
          heading.setAttribute('aria-hidden', 'true');
          heading.textContent = label || '';

          var inner = document.createElement('ul');
          inner.className = 'mst-search__list';
          inner.setAttribute('role', 'presentation');

          group.appendChild(heading);
          group.appendChild(inner);
          list.appendChild(group);
          return inner;
        }

        collectionLink(collection) {
          var link = document.createElement('a');
          link.className = 'mst-search__result mst-search__result--collection';
          link.href = collection.url;

          var title = document.createElement('span');
          title.className = 'mst-search__title';
          title.textContent = collection.title;
          link.appendChild(title);
          return link;
        }

        productLink(product) {
          var link = document.createElement('a');
          link.className = 'mst-search__result';
          link.href = product.url;

          var thumbSrc = this.thumbUrl(product.featured_image);
          if (thumbSrc) {
            var img = document.createElement('img');
            img.className = 'mst-search__thumb';
            img.src = thumbSrc;
            img.alt = '';
            img.width = 56;
            img.height = 56;
            img.loading = 'lazy';
            link.appendChild(img);
          } else {
            var ph = document.createElement('span');
            ph.className = 'mst-search__thumb mst-search__thumb--empty';
            ph.setAttribute('aria-hidden', 'true');
            link.appendChild(ph);
          }

          var title = document.createElement('span');
          title.className = 'mst-search__title';
          title.textContent = product.title;
          link.appendChild(title);

          var priceText = this.formatPrice(product.price);
          // Same "Od …" prefix as the product cards when variant prices differ.
          if (
            priceText &&
            this.dataset.fromPrice &&
            parseFloat(product.price_max) > parseFloat(product.price_min)
          ) {
            priceText = this.dataset.fromPrice.replace('[price]', priceText);
          }
          if (priceText) {
            var price = document.createElement('span');
            price.className = 'mst-search__price';
            price.textContent = priceText;
            link.appendChild(price);
          }

          return link;
        }

        render(products, collections, query, capped) {
          this.resultsEl.innerHTML = '';
          this.options = [];
          this.activeIndex = -1;
          this.input.removeAttribute('aria-activedescendant');

          if (!products.length && !collections.length) {
            this.input.setAttribute('aria-expanded', 'false');
            // ux-visual-19: shown once, in the status line (a second <p> repeated it).
            this.setStatus(this.dataset.noResults || '');
            return;
          }

          var list = document.createElement('ul');
          list.className = 'mst-search__list';
          list.id = 'MstPredictiveListbox';
          list.setAttribute('role', 'listbox');
          list.setAttribute('aria-label', this.dataset.listboxLabel || '');

          var self = this;
          // Without a matching collection the products stay flat, as before.
          var productParent = list;
          if (collections.length) {
            var collectionParent = this.addGroup(list, 'collections', this.dataset.collectionsLabel);
            collections.forEach(function (collection) {
              self.addOption(collectionParent, self.collectionLink(collection), 'mst-search__item--collection');
            });
            if (products.length) {
              productParent = this.addGroup(list, 'products', this.dataset.productsLabel);
            }
          }

          products.forEach(function (product) {
            self.addOption(productParent, self.productLink(product));
          });

          /* "View all" as the final option so it is arrow-key reachable. */
          var viewAll = document.createElement('a');
          viewAll.className = 'mst-search__result mst-search__viewall';
          viewAll.href = this.searchUrl + '?q=' + encodeURIComponent(query);
          viewAll.textContent = this.dataset.viewAll || '';
          this.addOption(list, viewAll, 'mst-search__item--all');

          this.resultsEl.appendChild(list);
          this.input.setAttribute('aria-expanded', 'true');

          var template = (capped && this.dataset.resultsCapped) || this.dataset.results || '';
          this.setStatus(template.replace('[count]', String(products.length + collections.length)));
        }

        onKeydown(event) {
          if (!this.options.length) return;

          if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.setActive((this.activeIndex + 1) % this.options.length);
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.setActive(
              (this.activeIndex - 1 + this.options.length) % this.options.length
            );
          } else if (event.key === 'Enter') {
            if (this.activeIndex > -1) {
              event.preventDefault();
              var link = this.options[this.activeIndex].querySelector('a');
              if (link) window.location.assign(link.href);
            }
          }
        }

        setActive(index) {
          var self = this;
          this.options.forEach(function (option, i) {
            var selected = i === index;
            option.setAttribute('aria-selected', selected ? 'true' : 'false');
            option.classList.toggle('is-selected', selected);
            if (selected) {
              self.input.setAttribute('aria-activedescendant', option.id);
              if (option.scrollIntoView) option.scrollIntoView({ block: 'nearest' });
            }
          });
          this.activeIndex = index;
        }
      }
    );
  }
})();
